/**
 * HTTP-клиент desktop-приложения.
 *
 * Единственное место, где приложение ходит в сеть: он собирает URL,
 * разбирает конверт `ApiResponse<T>` из `@aptile/shared` и переводит
 * неуспех в исключение. Экраны не знают ни про `fetch`, ни про формат
 * ответа — они ловят `ApiClientError` и показывают тост.
 */

import { LEARNING_LANGUAGE_HEADER, type ApiError, type ApiResponse } from '@aptile/shared';

import i18n from '../i18n';
import { useLearningStore } from '../store/learningStore';
import { deadline } from './deadline';

/**
 * Базовый адрес API.
 *
 * Пустая строка — явный признак «тот же origin»: так собирается веб-клиент
 * для Docker, где страницу и API отдаёт один nginx (см. `apps/desktop/nginx.conf`).
 * Если переменная не задана вовсе, берётся стандартный порт сервера
 * (`PORT` в его `env.ts`) — это режим разработки и Tauri.
 */
function resolveBaseUrl(): string {
  const configured: unknown = import.meta.env.VITE_API_URL;
  if (typeof configured === 'string') return configured.replace(/\/+$/, '');
  return 'http://localhost:3001';
}

const BASE_URL = resolveBaseUrl();

/**
 * Абсолютный адрес сервера для ссылок, которые грузит не `fetch`, а сам
 * браузер (например `<img src>`): путь от сервера начинается со `/` и без
 * базы превратился бы в адрес Vite-окна.
 */
export function apiUrl(path: string): string {
  return `${BASE_URL}${path}`;
}

/**
 * Параметры запроса берём из сигнатуры `fetch`, а не из глобального
 * `RequestInit`: последний есть только в типах DOM, и `no-undef` из
 * базового набора ESLint не считает его известным.
 */
type FetchInit = Parameters<typeof fetch>[1];

/** Параметры `apiRequest`: как у `fetch`, плюс срок ответа. */
export type ApiRequestInit = NonNullable<FetchInit> & {
  /**
   * Сколько ждать ответ целиком (токен, заголовки и тело), мс. Не дождались —
   * запрос отменяется и бросает `ApiClientError` с кодом `TIMEOUT`. Без срока
   * запрос ждёт сколько угодно: оборванная связь (сон ноутбука, прокси,
   * мобильная сеть) оставила бы экран в загрузке навсегда.
   */
  readonly timeoutMs?: number;
};

/** Ошибка обращения к API: и сетевой сбой, и ошибка бизнес-логики. */
export class ApiClientError extends Error {
  /** Код из конверта (`ApiError.code`) либо синтетический для сбоев сети. */
  readonly code: string;
  /** У ошибок `LIMIT_REACHED_*` — какой лимит тарифа исчерпан. */
  readonly limit: ApiError['limit'];

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiClientError';
    this.code = error.code;
    this.limit = error.limit;
  }
}

/**
 * Откуда брать токен доступа (`api/auth.ts`). Клиент не знает про стор:
 * провайдер подключается снаружи, и цикла импортов нет.
 */
export interface AuthProvider {
  readonly token: () => Promise<string>;
  /** Сервер отверг этот токен — следующий `token()` должен получить новый. */
  readonly invalidate: (token: string) => void;
}

let auth: AuthProvider | null = null;

export function configureAuth(provider: AuthProvider): void {
  auth = provider;
}

/** Токен доступа для соединений мимо `fetch` (WebSocket распознавания); `null` — не вошли. */
export async function accessToken(): Promise<string | null> {
  if (auth === null) return null;
  try {
    return await auth.token();
  } catch {
    return null;
  }
}

/** Без токена: вход, заявки в бету, картинки банка и онбординг гостя. */
const PUBLIC_PREFIXES = ['/api/auth/', '/api/beta', '/api/images/', '/api/guest/'] as const;

const isPublic = (path: string): boolean => PUBLIC_PREFIXES.some((prefix) => path.startsWith(prefix));

/** Сериализует тело запроса: экраны передают сюда обычные объекты. */
export function jsonBody(data: unknown): string {
  return JSON.stringify(data);
}

/** Ошибка лимита тарифа (`LIMIT_REACHED_*`): модалка тарифов подключается снаружи. */
type LimitHandler = (error: ApiClientError) => void;
let onLimit: LimitHandler | null = null;

export function configureLimitHandler(handler: LimitHandler): void {
  onLimit = handler;
}

/** Ошибка из потокового ответа: лимит тарифа открывает ту же модалку, что и обычный запрос. */
export function reportApiError(error: ApiClientError): ApiClientError {
  if (error.code.startsWith('LIMIT_REACHED_')) onLimit?.(error);
  return error;
}

/**
 * Запрос к API с токеном и языком ученика, без разбора ответа: для
 * двоичных данных (запись голоса, озвучка). Один повтор со свежим токеном
 * на 401; недоступный сервер — `ApiClientError` с `NETWORK_ERROR`.
 */
export async function apiFetch(path: string, init?: FetchInit): Promise<Response> {
  const { headers, ...rest } = init ?? {};
  const provider = isPublic(path) ? null : auth;

  const send = async (token: string | null): Promise<Response> => {
    try {
      return await fetch(`${BASE_URL}${path}`, {
        ...rest,
        headers: {
          'Content-Type': 'application/json',
          // Данные ученика — в его изучаемом языке: карта, карман, уроки, чат.
          ...(token === null ? {} : { Authorization: `Bearer ${token}`, [LEARNING_LANGUAGE_HEADER]: useLearningStore.getState().activeLanguage }),
          ...headers,
        },
      });
    } catch (cause) {
      // Отменили сами (срок вышел или экран ушёл) — причина уже `ApiClientError`.
      if (rest.signal?.aborted === true && rest.signal.reason instanceof ApiClientError) throw rest.signal.reason;
      throw new ApiClientError({
        code: 'NETWORK_ERROR',
        message: i18n.t('errors.network', { url: BASE_URL, cause: String(cause) }),
      });
    }
  };

  let token = provider === null ? null : await provider.token();
  let response = await send(token);
  // Токен истёк или сервер перезапущен с новым секретом — один повтор со свежим.
  if (provider !== null && token !== null && response.status === 401) {
    provider.invalidate(token);
    token = await provider.token();
    response = await send(token);
  }
  return response;
}

/**
 * Один запрос к API. Бросает `ApiClientError` при недоступном сервере,
 * не-JSON ответе, ошибке в конверте, истёкшем сроке (`TIMEOUT`) или отмене
 * (`ABORTED`) — вызывающий код обрабатывает единственный тип исключения.
 */
export async function apiRequest<T>(path: string, init?: ApiRequestInit): Promise<T> {
  const { timeoutMs, signal, ...rest } = init ?? {};
  const guard = deadline(signal, timeoutMs, (code) => new ApiClientError({ code, message: i18n.t(code === 'TIMEOUT' ? 'errors.timeout' : 'errors.aborted') }));
  try {
    return await Promise.race([send<T>(path, { ...rest, signal: guard.signal }), guard.stopped]);
  } finally {
    guard.dispose();
  }
}

async function send<T>(path: string, init: FetchInit): Promise<T> {
  const response = await apiFetch(path, init);

  let payload: ApiResponse<T>;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    if (init?.signal?.aborted === true && init.signal.reason instanceof ApiClientError) throw init.signal.reason;
    throw new ApiClientError({
      code: 'INVALID_RESPONSE',
      message: i18n.t('errors.invalidResponse', { status: response.status }),
    });
  }

  if (!payload.success) {
    const error = new ApiClientError(payload.error);
    // Лимит тарифа — модалка «перейди на тариф» поверх любого экрана.
    if (error.code.startsWith('LIMIT_REACHED_')) onLimit?.(error);
    throw error;
  }

  return payload.data;
}
